'use client';

import { useMemo } from 'react';
import { ArrowUpDownIcon, Tick01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { WebAppDraftStrategyType } from '@terra/shared-schemas-orchestrator';
import { Field } from '../../../../components/field';
import {
  Select,
  createSelectListCollection,
} from '../../../../components/select';
import { useDraftOnOpen } from '../../../application-general/useDraftOnOpen';
import { SettingsEditDialog } from '../../../settings/SettingsEditDialog';
import { isWebAppDraftStrategy } from './web-app-strategy';

export type WebAppStrategyDialogProps = {
  open: boolean;
  strategy: WebAppDraftStrategyType;
  loading: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (strategy: WebAppDraftStrategyType) => void;
};

export function WebAppStrategyDialog({
  open,
  strategy,
  loading,
  onOpenChange,
  onSave,
}: WebAppStrategyDialogProps) {
  const t = useTranslations(
    'designSystem.networkSettings.externalInventory.webApp',
  );
  const [draftStrategy, setDraftStrategy] = useDraftOnOpen({
    open,
    value: strategy,
  });
  const strategyCollection = useMemo(
    () =>
      createSelectListCollection({
        items: [
          { label: t('strategy.perOrigin'), value: 'PER_ORIGIN' },
          { label: t('strategy.perApex'), value: 'PER_APEX' },
        ],
      }),
    [t],
  );

  return (
    <SettingsEditDialog
      open={open}
      title={t('editStrategyTitle')}
      onOpenChange={onOpenChange}
      onSubmit={() => onSave(draftStrategy)}
      loading={loading}
    >
      <Field.Root>
        <Field.Label>{t('strategyLabel')}</Field.Label>
        <Select.Root
          positioning={{ sameWidth: true }}
          width="full"
          collection={strategyCollection}
          value={[draftStrategy]}
          onValueChange={(event) => {
            const nextStrategy = event.value[0];

            if (nextStrategy && isWebAppDraftStrategy(nextStrategy)) {
              setDraftStrategy(nextStrategy);
            }
          }}
        >
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText />
              <ArrowUpDownIcon size={14} />
            </Select.Trigger>
          </Select.Control>
          <Select.Positioner>
            <Select.Content>
              <Select.ItemGroup>
                {strategyCollection.items.map((item) => (
                  <Select.Item key={item.value} item={item}>
                    <Select.ItemText>{item.label}</Select.ItemText>
                    <Select.ItemIndicator>
                      <Tick01Icon size={14} />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.ItemGroup>
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
      </Field.Root>
    </SettingsEditDialog>
  );
}
